import React, { useState } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth, database } from '../firebase/config';
import { ref, get } from 'firebase/database';
import styled from 'styled-components';
import { Card, Button, Title, Input, FormGroup } from '../styles/GlobalStyles';
import connectingFoodLogo from '../assets/LogoCF_Branco.png';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';

const AdminLoginContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const AdminCard = styled(Card)`
  max-width: 450px;
  width: 100%;
  padding: 40px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
`;

const LogoImage = styled.img`
  width: 150px;
  height: auto;
  margin-bottom: 24px;
  display: block;
  margin-left: auto;
  margin-right: auto;
`;

const AdminTitle = styled(Title)`
  color: #2c3e50;
  margin-bottom: 8px;
  font-size: 2rem;
`;

const AdminSubtitle = styled.p`
  color: #6c757d;
  font-size: 1rem;
  margin-bottom: 32px;
  line-height: 1.5;
`;

const AdminFormGroup = styled(FormGroup)`
  text-align: left;
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #2c3e50;
  font-size: 14px;
`;

const AdminInput = styled(Input)`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  
  &:focus {
    border-color: #667eea;
    outline: none;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const AdminButton = styled(Button)`
  width: 100%;
  background: #667eea;
  border: none;
  padding: 14px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  margin-bottom: 20px;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background: #5a6fd8;
    transform: translateY(-1px);
  }
  
  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorText = styled.div`
  color: #dc3545;
  font-size: 14px;
  margin-bottom: 16px;
  text-align: center;
  padding: 12px;
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 6px;
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: #667eea;
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  margin-top: 16px;
  
  &:hover {
    color: #5a6fd8;
  }
`;

const ForgotPasswordLink = styled.button`
  background: none;
  border: none;
  color: #667eea;
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  margin-top: 12px;
  
  &:hover {
    color: #5a6fd8;
  }
`;

const SuccessText = styled.div`
  color: #28a745;
  font-size: 14px;
  margin-bottom: 16px;
  text-align: center;
  padding: 12px;
  background: #d4edda;
  border: 1px solid #c3e6cb;
  border-radius: 6px;
`;

const ResetContainer = styled.div`
  text-align: center;
`;

const ResetTitle = styled.h3`
  color: #2c3e50;
  margin-bottom: 16px;
  font-size: 1.5rem;
`;

const ResetDescription = styled.p`
  color: #6c757d;
  font-size: 14px;
  margin-bottom: 24px;
  line-height: 1.5;
`;

const AdminLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isResetMode, setIsResetMode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAdminAuth();

  const checkAdminStatus = async (uid: string): Promise<boolean> => {
    try {
      
      const adminRef = ref(database, `admin/${uid}`);
      const snapshot = await get(adminRef);
      const isAdmin = snapshot.exists();
      
      if (isAdmin) {
        
      } else {
        
      }
      return isAdmin;
    } catch (error) {
      
      return false;
    }
  };

  const getAuthErrorMessage = (errorCode: string): string => {
    switch (errorCode) {
      case 'auth/user-not-found':
        return 'Usuário não encontrado. Verifique o email digitado.';
      case 'auth/wrong-password':
        return 'Senha incorreta. Tente novamente.';
      case 'auth/invalid-email':
        return 'Email inválido. Verifique o formato do email.';
      case 'auth/user-disabled':
        return 'Esta conta foi desabilitada. Entre em contato com o suporte.';
      case 'auth/too-many-requests':
        return 'Muitas tentativas de login. Tente novamente mais tarde.';
      case 'auth/network-request-failed':
        return 'Erro de conexão. Verifique sua internet e tente novamente.';
      default:
        return 'Erro ao fazer login. Tente novamente.';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    
    

    try {
      if (!email.trim() || !password.trim()) {
        
        setError('Por favor, preencha todos os campos.');
        setLoading(false);
        return;
      }

      
      // Fazer login usando o contexto de admin
      await login(email, password);
      
      
      
      // Redirecionar para dashboard admin
      navigate('/admin/dashboard');
      
    } catch (error: any) {
      
      
      
      const errorMessage = getAuthErrorMessage(error.code);
      setError(errorMessage);
    } finally {
      setLoading(false);
      
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResetLoading(true);

    try {
      if (!resetEmail.trim()) {
        setError('Por favor, digite seu email.');
        setResetLoading(false);
        return;
      }

      
      await sendPasswordResetEmail(auth, resetEmail);
      
      
      setResetSuccess(true);
      setError('');
    } catch (error: any) {
      
      const errorMessage = getAuthErrorMessage(error.code);
      setError(errorMessage);
    } finally {
      setResetLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setIsResetMode(false);
    setResetSuccess(false);
    setError('');
    setResetEmail('');
  };

  const handleBackToMain = () => {
    navigate('/');
  };

  return (
    <AdminLoginContainer>
      <AdminCard>
        <LogoImage src={connectingFoodLogo} alt="Connecting Food" />
        
        {!isResetMode ? (
          // Tela de Login
          <>
            <AdminTitle>🛡️ Acesso Administrativo</AdminTitle>
            <AdminSubtitle>
              Área restrita para administradores do sistema.
              Faça login com suas credenciais administrativas.
            </AdminSubtitle>
            
            <form onSubmit={handleSubmit}>
              <AdminFormGroup>
                <Label>Email</Label>
                <AdminInput
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@connectingfood.com"
                  required
                  autoComplete="email"
                />
              </AdminFormGroup>

              <AdminFormGroup>
                <Label>Senha</Label>
                <AdminInput
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Sua senha administrativa"
                  required
                  autoComplete="current-password"
                />
              </AdminFormGroup>

              {error && <ErrorText>{error}</ErrorText>}

              <AdminButton type="submit" disabled={loading}>
                {loading ? '🔄 Verificando...' : '🔐 Entrar como Admin'}
              </AdminButton>
            </form>
            
            <ForgotPasswordLink onClick={() => setIsResetMode(true)}>
              Esqueci minha senha
            </ForgotPasswordLink>
            
            <BackLink onClick={handleBackToMain}>
              ← Voltar para o site principal
            </BackLink>
          </>
        ) : (
          // Tela de Recuperação de Senha
          <ResetContainer>
            {!resetSuccess ? (
              <>
                <ResetTitle>🔑 Recuperar Senha</ResetTitle>
                <ResetDescription>
                  Digite seu email administrativo para receber as instruções de recuperação de senha.
                </ResetDescription>
                
                <form onSubmit={handlePasswordReset}>
                  <AdminFormGroup>
                    <Label>Email Administrativo</Label>
                    <AdminInput
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      placeholder="admin@connectingfood.com"
                      required
                      autoComplete="email"
                    />
                  </AdminFormGroup>

                  {error && <ErrorText>{error}</ErrorText>}

                  <AdminButton type="submit" disabled={resetLoading}>
                    {resetLoading ? '📧 Enviando...' : '📧 Enviar Email de Recuperação'}
                  </AdminButton>
                </form>
                
                <BackLink onClick={handleBackToLogin}>
                  ← Voltar para o login
                </BackLink>
              </>
            ) : (
              <>
                <ResetTitle>✅ Email Enviado!</ResetTitle>
                <SuccessText>
                  📧 Enviamos as instruções de recuperação de senha para seu email.
                  Verifique sua caixa de entrada e siga as instruções para redefinir sua senha.
                </SuccessText>
                <ResetDescription>
                  Não recebeu o email? Verifique sua pasta de spam ou tente novamente.
                </ResetDescription>
                
                <AdminButton onClick={handleBackToLogin}>
                  ← Voltar para o Login
                </AdminButton>
              </>
            )}
          </ResetContainer>
        )}
        
        <div style={{ marginTop: '24px', fontSize: '12px', color: '#6c757d', borderTop: '1px solid #dee2e6', paddingTop: '16px' }}>
          <p>⚠️ Acesso restrito apenas para administradores autorizados</p>
        </div>
      </AdminCard>
    </AdminLoginContainer>
  );
};

export default AdminLogin;